import {useIntl} from 'react-intl';
import Hotkeys from 'react-hot-keys';
import debounce from 'lodash/debounce';
import {Alert} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import {Toolbar, Box} from '@material-ui/core';
import {PageListNavigation} from './PageListNavigation';

import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';

import {getOriginUrl} from '../helpers';
import {Navigation} from '../Navigation';
import {ImageThumbView} from './ImageThumbView';
import {PageListContext} from './PageListProvider';
import {useSegmentationContext} from './model/context';
import {AnnotatorProvider} from './Annotator/Provider';
import {DATASET_SUB_OP as OP} from '../../../config/url';
import {useAppDispatch, RootState} from '../../../store';
import {toast} from '../../../base-components/StudioToast';
import {startAutoLabel, stopAutoLabel} from '../../../store/iat';
import {SegmentationAnnotatorView} from './SegmentationAnnotatorView';
import {BoxLabelProps, Point, PolyLabelProps} from './Annotator/types';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {
  ImageType,
  CategoryType,
  AnnotationType,
  DataPrepareTypeStr,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './SegmentationPrepare.scss';

const UPDATE_PREDICTIONS_DEBOUNCE = 1_000;

export type SegmentationPrepareProps = {
  projectId: string;
  dataPreparationContainerId?: string;

  images?: ImageType[];
  disableBack: boolean;
  disableForward: boolean;
  labels?: CategoryType[];
  origin: DataPreparationOrigin;
  labelsMap?: {
    [key: string]: CategoryType;
  };

  onBack: () => void;
  onUpdateAnnotations: (annotations: AnnotationType[]) => void;
  onChange?: (annotationId: string, annotation: PolyLabelProps | BoxLabelProps) => void;
  onForward: (
    mergedPredictionAnnotations: AnnotationType[],
    discardedPredictionAnnotations: AnnotationType[]
  ) => void;
};

export const SegmentationPrepare = ({
  origin,
  images,
  projectId,
  labels = [],
  disableBack,
  disableForward,
  dataPreparationContainerId,

  onBack,
  onUpdateAnnotations,
  onForward = () => {},
}: SegmentationPrepareProps) => {
  const intl = useIntl();
  const pointsHistory = useRef<Point[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const SegmentationAnnotations = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );

  const {
    setProjectId,
    projectId: segmentationProjectId,

    setDataPreparationContainerId,
    dataPreparationContainerId: segmentationDataPreparationContainerId,

    shutdownSegmentationAPI,
    initializeSegmentationAPI,

    activeImageId,
    setActiveImageId,

    segmentationList,
    activeSegmentation,
    setInitialSegments,
    clearSegmentationData,
    updateActiveSegmentationResults,

    addPoint,
    removePoint,

    removePolygon,
  } = useSegmentationContext();

  const {
    page,
    sortedList,
    itemsPerPageCount,

    setPage,
  } = useContext(PageListContext);

  useEffect(
    function updateSegmentationContextDatapreparationContainerId() {
      if (dataPreparationContainerId) {
        setDataPreparationContainerId(dataPreparationContainerId);
      } else {
        toast.error(
          intl.formatMessage({id: 'dataPrep.segmentation.datasetContainerIdMissing'})
        );
      }
    },
    [intl, dataPreparationContainerId, setDataPreparationContainerId]
  );

  useEffect(
    function updateSegmentationContextProjectId() {
      setProjectId(projectId);
    },
    [projectId, setProjectId]
  );

  useEffect(
    function setSegmentsWhenAnnotationsChange() {
      if (initialized && SegmentationAnnotations) {
        clearSegmentationData().then(() => {
          setInitialSegments(SegmentationAnnotations);
        });
      }
    },
    [initialized, SegmentationAnnotations, setInitialSegments, clearSegmentationData]
  );

  useEffect(
    function initializeSegmentationAPIAndSetInitialAnnotationsWhenComponentOnInit() {
      if (
        !initialized &&
        segmentationProjectId &&
        segmentationDataPreparationContainerId
      ) {
        setInitialized(true);
        initializeSegmentationAPI();
      }
    },
    [
      initialized,
      segmentationProjectId,
      segmentationDataPreparationContainerId,
      initializeSegmentationAPI,
    ]
  );

  const isIATBusy = useSelector((state: RootState) => state.iat.isBusy);
  const autoLabelReady = useSelector((state: RootState) => state.iat.autoLabelReady);
  const isAutoLabel = useSelector((state: RootState) => state.dataPrepare.isAutoLabel);
  const annotationData = useSelector((state: RootState) => {
    return state.dataPrepare.annotationData;
  });

  const appDispatch = useAppDispatch();

  const selectedImage = useMemo(
    () => images?.find(image => String(image.id) === activeImageId),
    [images, activeImageId]
  );

  // TODO: improve this legacy code
  // Start/Stop Autolabel
  useEffect(
    function handleStartAndStopForAutoLabel() {
      if (
        projectId &&
        dataPreparationContainerId &&
        isAutoLabel &&
        !autoLabelReady &&
        !isIATBusy
      ) {
        appDispatch(
          startAutoLabel({
            projectId,
            resourceId: dataPreparationContainerId,
            type: DataPrepareTypeStr.SEGMENTATION,
          })
        );
      }

      return () => {
        if (autoLabelReady && annotationData) {
          appDispatch(stopAutoLabel({projectId, resourceId: dataPreparationContainerId}));
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoLabelReady, projectId, isAutoLabel, appDispatch, dataPreparationContainerId]
  );

  // TODO: improve this legacy code
  // Set default image selection
  useEffect(() => {
    if (sortedList.length) {
      if (selectedImage) {
        // set to local storage saved data
        setSelectedIndex(sortedList?.findIndex(i => i.data.id === selectedImage?.id));
      } else {
        // set to first image if there is none
        setActiveImageId(String(sortedList[0].data.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedList, selectedImage]);

  const {annotationsMap, annotationsArray: annotations} = useMemo(() => {
    const annotationsMap: {[imageId: number]: AnnotationType[]} = {};
    const annotationsArray: AnnotationType[] = [];

    segmentationList.getAllSegmentations().forEach(segmentation => {
      const imageId = Number(segmentation.imageId);
      const polygons = segmentation.getPredictions();

      if (!annotationsMap[imageId]) {
        annotationsMap[imageId] = [];
      }

      polygons.forEach(polygon => {
        const annotation: AnnotationType = {
          id: Number(polygon.id.split('_')[1]),
          image_id: imageId,
          category_id: polygon.label
            ? labels.find(label => label.name === polygon.label)?.id
            : undefined,
          segmentation: polygon.points,
        };
        annotationsMap[imageId].push(annotation);
        annotationsArray.push(annotation);
      });
    });

    return {annotationsMap, annotationsArray};
  }, [segmentationList, labels]);

  const [storedAnnotations, setStoredAnnotations] = useState<string>('');

  useEffect(
    function autoSaveAnnotations() {
      const annotationsToStore = JSON.stringify(annotations);

      if (annotationsToStore !== storedAnnotations) {
        setStoredAnnotations(annotationsToStore);
        onUpdateAnnotations(annotations);
      }
    },
    [annotations, storedAnnotations, onUpdateAnnotations]
  );

  const setSelection = useCallback(
    (image: ImageType) => {
      const index = images?.findIndex(storedImage => storedImage.id === image?.id);

      if (index !== undefined && index >= 0) {
        setSelectedIndex(index);
        setActiveImageId(String(image.id));
      }
    },
    [images, setActiveImageId]
  );

  const changeSelectedImage = useCallback(
    (index: number) => {
      if (sortedList && index >= 0 && index < sortedList.length) {
        const image = sortedList[index];

        if (image) {
          setActiveImageId(String(image.data.id));
          setSelectedIndex(index);
        }
      }
    },
    [sortedList, setActiveImageId]
  );

  const prevImage = useCallback(() => {
    const newIndex = selectedIndex - 1;
    if (newIndex >= 0) {
      changeSelectedImage(newIndex);
    }
  }, [selectedIndex, changeSelectedImage]);

  const nextImage = useCallback(() => {
    const newIndex = selectedIndex + 1;
    const totalImages = images ? images.length : 0;

    if (newIndex < totalImages) {
      changeSelectedImage(newIndex);
    }
  }, [selectedIndex, changeSelectedImage, images]);

  const hasdataToSent = useMemo(
    () =>
      segmentationList.getDataToSent().find(segmentation => segmentation.polygons.length),
    [segmentationList]
  );

  const onSubmit = useCallback(() => {
    const discardedPredictionsAnnotations: AnnotationType[] = [];
    const mergedPredictionsAnnotations: AnnotationType[] = annotations;

    onForward(mergedPredictionsAnnotations, discardedPredictionsAnnotations);
    shutdownSegmentationAPI();
  }, [onForward, shutdownSegmentationAPI, annotations]);

  const undoLastMaskOrFreePoint = useCallback(() => {
    if (selectedImage) {
      const imageId = String(selectedImage.id);

      if (pointsHistory.current.length > 0) {
        const lastPoint = pointsHistory.current.pop();

        if (imageId && lastPoint) {
          removePoint(imageId, lastPoint);
        }
      } else {
        const lastSegmentation = segmentationList.getSegmentationByImageId(imageId);
        const polygons = lastSegmentation?.getPolygons({includeEmpty: false});

        if (polygons?.length) {
          removePolygon(imageId, polygons[polygons.length - 1].id);
        }
      }
    }
  }, [selectedImage, segmentationList, pointsHistory, removePoint, removePolygon]);

  const hotKey: {[key: string]: Function} = useMemo(
    () => ({
      left: prevImage,
      right: nextImage,
      del: undoLastMaskOrFreePoint,
      backspace: undoLastMaskOrFreePoint,
    }),
    [prevImage, nextImage, undoLastMaskOrFreePoint]
  );

  const onFreePointCreated = useCallback(
    async (point: Point, isRightClick: boolean) => {
      if (selectedImage) {
        const imageId = String(selectedImage.id);

        pointsHistory.current.push(point);
        await addPoint(imageId, point, !isRightClick);
      }
    },
    [selectedImage, addPoint]
  );

  const onDeleteFreePoint = useCallback(
    async point => {
      if (selectedImage) {
        const imageId = String(selectedImage.id);

        if (await removePoint(imageId, point)) {
          const index = pointsHistory.current.lastIndexOf(point);

          if (index > -1) {
            pointsHistory.current.splice(index, 1);
          }
        }
      }
    },
    [selectedImage, removePoint]
  );

  const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>();

  const debouncedUpdate = useCallback(() => {
    if (!debouncedUpdateRef.current) {
      debouncedUpdateRef.current = debounce(async () => {
        await updateActiveSegmentationResults();
      }, UPDATE_PREDICTIONS_DEBOUNCE);
    }
    debouncedUpdateRef.current();
  }, [updateActiveSegmentationResults]);

  useEffect(
    function shouldTriggerPredictionUpdate() {
      if (activeSegmentation?.hasDataChanged()) {
        debouncedUpdate();
      }

      return () => {
        debouncedUpdateRef.current?.cancel();
      };
    },
    [debouncedUpdate, activeSegmentation]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      changeSelectedImage((newPage - 1) * itemsPerPageCount);
    },
    [setPage, changeSelectedImage, itemsPerPageCount]
  );

  const onPredictionCompleted = useCallback(() => {
    pointsHistory.current = [];
  }, []);

  return (
    <Hotkeys
      keyName="right,left,backspace,del"
      onKeyUp={(keyName: string) => hotKey[keyName]()}
    >
      <div className="segmentation-view">
        <Toolbar
          className="data-prepare__header segmentation-view__header"
          variant="dense"
        >
          <Box className="segmentation-view__pagination">
            <PageListNavigation onChange={onPageChange} />
          </Box>
          <Box className="segmentation-view__helper-message-container">
            <Alert severity="info" className="segmentation-view__alert">
              {intl.formatMessage({
                id: 'dataPrep.segmentation.segmentationInformation',
              })}
            </Alert>
          </Box>
          <Navigation
            onBack={onBack}
            onForward={onSubmit}
            disableBack={disableBack}
            disableForward={disableForward || !hasdataToSent}
          />
        </Toolbar>
        <div className="segmentation-view__content">
          <AnnotatorProvider>
            <ImageThumbView
              images={images}
              origin={origin}
              projectId={projectId}
              selection={selectedImage}
              onClick={setSelection}
              getImageAnnotationsQuantity={(imageId: number) =>
                annotationsMap[imageId]?.length || 0
              }
            />
            <SegmentationAnnotatorView
              image={selectedImage}
              src={
                selectedImage
                  ? `${getOriginUrl(origin)(projectId, OP.GET_BY_NAME)}?name=${
                      selectedImage.file_name
                    }`
                  : ''
              }
              labels={labels.map(el => ({
                id: el.id,
                label: el.name,
              }))}
              onDeleteFreePoint={onDeleteFreePoint}
              onFreePointCreated={onFreePointCreated}
              onPredictionCompleted={onPredictionCompleted}
            />
          </AnnotatorProvider>
        </div>
      </div>
    </Hotkeys>
  );
};
